import { useEffect, useState } from 'react';
import CandidateDetailPanel from '../components/CandidateDetailPanel';
import DocumentsPanel from '../components/DocumentsPanel';
import ImportPanel from '../components/ImportPanel';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Eye,
  FileSpreadsheet,
  Loader2,
  Paperclip,
  Pencil,
  Trash2,
  Phone,
  Search,
  Send,
  Upload,
  X,
} from 'lucide-react';
import { client, errorMessage } from '../api/client';
import CustomFieldInputs, { useCustomFields } from '../components/CustomFieldInputs';
import { toast } from 'sonner';
import DeleteRecordDialog from '../components/DeleteRecordDialog';
import RecruiterFilter from '../components/RecruiterFilter';
import { isCompanyAdmin, isSupervisor, useAuth } from '../store/auth';
import { downloadExport, exportErrorMessage } from '../lib/exportDownload';
import { formatPhone, telHref } from '../lib/phone';
import { seesOthersWork } from '../lib/teamView';
import { useDebounced } from '../lib/useDebounced';
import {
  formatFilterDate,
  parseFilterDate,
  sanitiseFilterDateInput,
} from '../lib/filterDate';
import { titleCaseName, titleCaseNameOr } from '../lib/titleCase';
import ClientSelect from '../components/ClientSelect';
import type {
  Candidate,
  CandidateCertificate,
  Client as ClientRow,
  CustomValues,
  InterviewMode,
  JobOpeningWithPipeline,
  Page,
  ResumeChoice,
  SubmissionStatus,
} from '../types';

/**
 * Correcting the four things about a candidate that go stale.
 *
 * Name and mobile are shown but locked. The phone is the deduplication key
 * within a company, so editing it here would silently merge or split people;
 * that belongs somewhere it can be done deliberately, not in a row dialog.
 *
 * A modal rather than inline editing, matching the Joining Tracker's dialog:
 * four fields with two different destinations is more than a cell can explain.
 */
function EditDialog({
  candidate,
  clients,
  saving,
  canSeeDob,
  canSeeSalary,
  onCancel,
  onSave,
}: {
  candidate: Candidate;
  clients: ClientRow[];
  saving: boolean;
  onCancel: () => void;
  canSeeDob: boolean;
  canSeeSalary: boolean;
  onSave: (values: {
    altPhone: string;
    email: string;
    certificates: string[];
    hadPt: boolean;
    hadEsi: boolean;
    hadTds: boolean;
    qualification: string;
    experienceMonths: string;
    currentLocation: string;
    preferredLocation: string;
    currentCompany: string;
    designation: string;
    currentlyEmployed: string;
    currentSalary: string;
    expectedSalary: string;
    noticePeriodDays: string;
    dateOfBirth: string;
    clientId: string;
    customValues: CustomValues;
  }) => void;
}) {
  // Fetched here rather than passed in: the dialog is the only thing that
  // needs them, and the query is cached across opens.
  const customFields = useCustomFields('candidate');
  const [customValues, setCustomValues] = useState<CustomValues>(
    candidate.customValues ?? {},
  );
  const [qualification, setQualification] = useState(candidate.qualification ?? '');
  const [experienceMonths, setExperienceMonths] = useState(
    candidate.experienceMonths == null ? '' : String(candidate.experienceMonths),
  );
  const [currentLocation, setCurrentLocation] = useState(
    candidate.currentLocation ?? '',
  );
  // Every one of these was already accepted by the API and simply never shown.
  // date_of_birth and notice_period_days additionally could not be corrected
  // at all until CandidateUpdate stopped omitting them.
  const [preferredLocation, setPreferredLocation] = useState(
    candidate.preferredLocation ?? '',
  );
  const [currentCompany, setCurrentCompany] = useState(candidate.currentCompany ?? '');
  const [designation, setDesignation] = useState(candidate.designation ?? '');
  //: Three states, and the empty string is the third. "Not asked" is not "no"
  //: — it decides whether the company field reads Present or Previous, and
  //: guessing would put a job title under the wrong heading.
  const [currentlyEmployed, setCurrentlyEmployed] = useState(
    candidate.currentlyEmployed === null || candidate.currentlyEmployed === undefined
      ? ''
      : String(candidate.currentlyEmployed),
  );
  const [altPhone, setAltPhone] = useState(candidate.altPhone ?? '');
  const [email, setEmail] = useState(candidate.email ?? '');
  const [hadPt, setHadPt] = useState(candidate.hadPt ?? false);
  const [hadEsi, setHadEsi] = useState(candidate.hadEsi ?? false);
  const [hadTds, setHadTds] = useState(candidate.hadTds ?? false);

  /**
   * The certificates already on file, loaded once the dialog opens.
   *
   * Held as names and diffed on save. The endpoints take one row at a time, so
   * the alternative is writing each add and delete the moment it is clicked —
   * which would leave Cancel undoing some of the dialog and not the rest.
   */
  const existingCertificates = useQuery<CandidateCertificate[]>({
    queryKey: ['candidates', candidate.id, 'certificates'],
    queryFn: async () =>
      (await client.get(`/api/v1/candidates/${candidate.id}/certificates`)).data,
  });
  const [certificates, setCertificates] = useState<string[] | null>(null);
  const [certificateDraft, setCertificateDraft] = useState('');
  // Seeded from the server the first time it answers, then owned locally.
  const loadedCertificates = certificates
    ?? (existingCertificates.data ?? []).map((c) => c.name);

  function addCertificate() {
    const name = certificateDraft.trim();
    // Silently ignoring a duplicate rather than warning: the list is the
    // answer to "which do they hold", and holding one twice is not a state.
    if (name && !loadedCertificates.includes(name)) {
      setCertificates([...loadedCertificates, name]);
    }
    setCertificateDraft('');
  }

  const [currentSalary, setCurrentSalary] = useState(candidate.currentSalary ?? '');
  const [expectedSalary, setExpectedSalary] = useState(candidate.expectedSalary ?? '');
  //: A stored notice that matches none of the buckets — an import, or a number
  //: typed before this was a dropdown. Kept as its own option so opening the
  //: dialog and pressing Save does not round it into a range nobody chose.
  const existingNotice =
    candidate.noticePeriodDays !== null
    && candidate.noticePeriodDays !== undefined
    && !NOTICE_PERIODS.some((o) => o.days === candidate.noticePeriodDays)
      ? candidate.noticePeriodDays
      : null;
  const [noticePeriodDays, setNoticePeriodDays] = useState(
    candidate.noticePeriodDays == null ? '' : String(candidate.noticePeriodDays),
  );
  const [dateOfBirth, setDateOfBirth] = useState(candidate.dateOfBirth ?? '');
  const [clientId, setClientId] = useState(candidate.latestCallClientId ?? '');

  const noCall = !candidate.latestCallId;

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4">
      {/* Sized and structured for the number of fields it actually has.
          It was max-w-md, one column, no height cap — fine for four fields and
          not fine at twelve: on a laptop the Save button sat below the fold
          with nothing to scroll. Header and footer are fixed and only the
          middle scrolls, so Save stays reachable however long the form grows.
          A super admin can add custom fields to it at any time. */}
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl">
        <div className="border-b border-gray-200 px-5 py-4">
          <h3 className="text-sm font-semibold text-gray-900">
            Edit {titleCaseName(candidate.fullName)}
          </h3>
          {/* Said rather than merely greyed, so nobody hunts for the control. */}
          <p className="mt-0.5 text-xs text-gray-500">
            Name and mobile are fixed here — the number identifies the candidate.
          </p>
        </div>

        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            onSave({
              altPhone,
              email,
              certificates: loadedCertificates,
              hadPt,
              hadEsi,
              hadTds,
              qualification,
              experienceMonths,
              currentLocation,
              preferredLocation,
              currentCompany,
              designation,
              currentlyEmployed,
              currentSalary,
              expectedSalary,
              noticePeriodDays,
              dateOfBirth,
              clientId,
              customValues,
            });
          }}
        >
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
            {/* Grouped, because twelve unlabelled boxes in a column is a list
                to get through rather than a form to fill in. */}
            <section>
              <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                Contact Details
              </h4>
              <div className="grid gap-3 sm:grid-cols-2">
                {/* Shown, not editable. The number is the deduplication key
                    within a company, so changing it here would silently merge
                    or split two people — that belongs somewhere it can be done
                    deliberately, not in a row dialog. */}
                <div>
                  <label className="label">Full Name</label>
                  <p className="input bg-gray-50 text-gray-500">
                    {titleCaseName(candidate.fullName)}
                  </p>
                </div>
                <div>
                  <label className="label">Mobile Number</label>
                  <p className="input bg-gray-50 text-gray-500">
                    {formatPhone(candidate.phone) ?? '—'}
                  </p>
                </div>

                <div>
                  <label className="label" htmlFor="ed-alt">
                    Alternative Number
                  </label>
                  <input
                    spellCheck={false}
                    id="ed-alt"
                    className="input"
                    inputMode="numeric"
                    placeholder="10 digits"
                    maxLength={20}
                    value={altPhone}
                    onChange={(e) => setAltPhone(e.target.value)}
                  />
                </div>

                <div>
                  <label className="label" htmlFor="ed-email">Email ID</label>
                  <input
                    spellCheck={false}
                    id="ed-email"
                    type="email"
                    className="input"
                    maxLength={255}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                {/* Company Admin and Owner only, matching the API. Rendering it
                    for anybody else would show a blank box that saves a blank —
                    the server ignores writes to a field the caller cannot read,
                    so the control would silently do nothing. */}
                {canSeeDob && (
                  <div>
                    <label className="label" htmlFor="ed-dob">Date of Birth</label>
                    <input
                      id="ed-dob"
                      type="date"
                      className="input"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </section>

            <section>
              <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                Education Details
              </h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="ed-qual">
                    Highest Qualification
                  </label>
                  <input
                    spellCheck={false}
                    id="ed-qual"
                    className="input"
                    maxLength={160}
                    value={qualification}
                    onChange={(e) => setQualification(e.target.value)}
                  />
                </div>

                <div>
                  <label className="label" htmlFor="ed-designation">
                    Designation / Position
                  </label>
                  <input
                    spellCheck={false}
                    id="ed-designation"
                    className="input"
                    maxLength={160}
                    placeholder="What they do now"
                    value={designation}
                    onChange={(e) => setDesignation(e.target.value)}
                  />
                </div>

                {/* Rows, not a text box. These are the same certificates the
                    employee record keeps, so they survive the hire instead of
                    being retyped — which means each one is a claim with a name
                    rather than a line in a paragraph.

                    Staged, and written on Save with everything else. Applying
                    them immediately would make Cancel a lie about half the
                    dialog. */}
                <div className="sm:col-span-2">
                  <label className="label" htmlFor="ed-cert">Certifications</label>
                  {loadedCertificates.length > 0 && (
                    <ul className="mb-2 flex flex-wrap gap-1.5">
                      {loadedCertificates.map((name) => (
                        <li
                          key={name}
                          className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                        >
                          {name}
                          <button
                            type="button"
                            className="text-gray-400 hover:text-red-600"
                            title={'Remove ' + name}
                            onClick={() =>
                              setCertificates(
                                loadedCertificates.filter((c) => c !== name),
                              )
                            }
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex gap-2">
                    <input
                      spellCheck={false}
                      id="ed-cert"
                      className="input"
                      maxLength={200}
                      placeholder="e.g. RN Licence, BLS"
                      value={certificateDraft}
                      onChange={(e) => setCertificateDraft(e.target.value)}
                      onKeyDown={(e) => {
                        // Enter adds the certificate rather than submitting the
                        // whole dialog, which is what it would otherwise do.
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCertificate();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn-ghost whitespace-nowrap text-xs"
                      onClick={addCertificate}
                    >
                      Add
                    </button>
                  </div>
                </div>

                <div>
                  <label className="label" htmlFor="ed-employed">
                    Currently Employed
                  </label>
                  <select
                    id="ed-employed"
                    className="input"
                    value={currentlyEmployed}
                    onChange={(e) => setCurrentlyEmployed(e.target.value)}
                  >
                    {/* Blank first, and it stays selected until somebody
                        chooses. Defaulting to "No" would assert every record
                        nobody has asked about is out of work. */}
                    <option value="">Not asked</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>

                <div>
                  <label className="label" htmlFor="ed-company">
                    {/* The one company column, labelled by the flag beside it.
                        Two columns would let the name and its currency
                        disagree; one column plus a label cannot. */}
                    {currentlyEmployed === 'true'
                      ? 'Present company'
                      : currentlyEmployed === 'false'
                        ? 'Previous company'
                        : 'Company'}
                  </label>
                  <input
                    spellCheck={false}
                    id="ed-company"
                    className="input"
                    maxLength={160}
                    value={currentCompany}
                    onChange={(e) => setCurrentCompany(e.target.value)}
                  />
                </div>

                <div>
                  <label className="label" htmlFor="ed-loc">Staying Location</label>
                  <input
                    spellCheck={false}
                    id="ed-loc"
                    className="input"
                    maxLength={160}
                    value={currentLocation}
                    onChange={(e) => setCurrentLocation(e.target.value)}
                  />
                </div>

                <div>
                  <label className="label" htmlFor="ed-pref">Preferred Location</label>
                  <input
                    spellCheck={false}
                    id="ed-pref"
                    className="input"
                    maxLength={160}
                    value={preferredLocation}
                    onChange={(e) => setPreferredLocation(e.target.value)}
                  />
                </div>

                <div>
                  <label className="label" htmlFor="ed-exp">
                    Experience (Months)
                  </label>
                  <input
                    id="ed-exp"
                    type="number"
                    min={0}
                    max={720}
                    className="input"
                    value={experienceMonths}
                    onChange={(e) => setExperienceMonths(e.target.value)}
                  />
                </div>
              </div>
            </section>

            <section>
              <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                Salary Details
              </h4>
              <div className="grid gap-3 sm:grid-cols-2">
                {/* Company Admin, Owner and the recruiter who owns the record.
                    Hidden rather than disabled for the same reason as the date
                    of birth: the server ignores the write, so an editable-
                    looking box would quietly discard what was typed. */}
                {canSeeSalary && (
                  <div className="sm:col-span-2">
                    <label className="label" htmlFor="ed-cursal">
                      Present Salary
                    </label>
                    <input
                      id="ed-cursal"
                      className="input"
                      inputMode="decimal"
                      placeholder="Take home salary"
                      value={currentSalary}
                      onChange={(e) => setCurrentSalary(e.target.value)}
                    />
                    {/* Beside the salary because that is where a recruiter
                        asks about them, but a fact about the candidate rather
                        than about the figure: somebody who has had all three
                        deducted has been on a compliant payroll before, which
                        is what ESI and PF continuity depends on at onboarding. */}
                    <fieldset className="mt-2">
                      <legend className="text-xs text-gray-500">
                        Had At a Previous Employer
                      </legend>
                      <div className="mt-1 flex flex-wrap gap-4">
                        <label className="flex items-center gap-1.5 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={hadPt}
                            onChange={(e) => setHadPt(e.target.checked)}
                          />
                          PT
                        </label>
                        <label className="flex items-center gap-1.5 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={hadEsi}
                            onChange={(e) => setHadEsi(e.target.checked)}
                          />
                          ESI
                        </label>
                        <label className="flex items-center gap-1.5 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={hadTds}
                            onChange={(e) => setHadTds(e.target.checked)}
                          />
                          TDS
                        </label>
                      </div>
                    </fieldset>
                  </div>
                )}

                <div>
                  <label className="label" htmlFor="ed-expsal">
                    Expected Salary
                  </label>
                  <input
                    id="ed-expsal"
                    className="input"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={expectedSalary}
                    onChange={(e) => setExpectedSalary(e.target.value)}
                  />
                </div>

                <div>
                  <label className="label" htmlFor="ed-notice">Notice Period</label>
                  <select
                    id="ed-notice"
                    className="input"
                    value={noticePeriodDays}
                    onChange={(e) => setNoticePeriodDays(e.target.value)}
                  >
                    <option value="">Not asked</option>
                    {NOTICE_PERIODS.map((option) => (
                      <option key={option.days} value={String(option.days)}>
                        {option.label}
                      </option>
                    ))}
                    {/* A value that predates the dropdown — an import, or a
                        number somebody typed before this was a list. Offered
                        as its own option so opening the dialog and saving does
                        not quietly round it into a bucket it was never in. */}
                    {existingNotice !== null && (
                      <option value={String(existingNotice)}>
                        {existingNotice} days
                      </option>
                    )}
                  </select>
                </div>
              </div>
            </section>

            <section>
              <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                Latest Call
              </h4>
              <label className="label" htmlFor="ed-client">Client</label>
              <select
                id="ed-client"
                className="input"
                value={clientId}
                disabled={noCall}
                onChange={(e) => setClientId(e.target.value)}
              >
                <option value="">No client</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {/* This one field writes to the call, not the candidate. Worth
                  saying: it changes what the Daily Calls Report shows for that
                  row too. Its own section for the same reason. */}
              <p className="mt-1 text-xs text-gray-500">
                {noCall
                  ? 'No call on file, so there is nothing to attach a client to.'
                  : 'Set on the most recent call, which the calls report also shows.'}
              </p>
            </section>

            {/* What a super admin added, in its own section rather than
                interleaved: these are additional to the record, not part of its
                shape. Section and heading both vanish when none are defined, so
                a company using no custom fields sees nothing at all. */}
            {(customFields.data?.items ?? []).length > 0 && (
              <section>
                <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                  Additional Fields
                </h4>
                <div className="grid gap-3 sm:grid-cols-2">
                  <CustomFieldInputs
                    fields={customFields.data?.items ?? []}
                    values={customValues}
                    onChange={setCustomValues}
                    idPrefix="ed"
                  />
                </div>
              </section>
            )}
          </div>

          {/* Outside the scrolling area, so Save never drifts off the bottom. */}
          <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
            <button type="button" className="btn-ghost" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Put a candidate in front of a client, from the list rather than from a call.
 *
 * The same submission the Calls page Track button creates, through the same
 * endpoint, because every candidate here has a latest call and the submission
 * has to belong to whoever made it — not to whoever pressed the button.
 *
 * Position is asked for here and not on the Calls page version. That form used
 * to collect it and no longer does, so without this every new submission would
 * carry a blank role.
 *
 * Choosing a client now answers the Position question wherever it can be
 * answered. One open requirement fills the box and names itself on the
 * submission; several offer a picker; none leaves the box empty and blocks
 * nothing — a recruiter must not be stopped from putting somebody forward
 * because the requirement has not been logged yet.
 *
 * The three-way rule is the server's, not this form's: `_sole_open_requirement`
 * has resolved a lone requirement for every caller of
 * `POST /calls/{id}/submit` since before this dialog existed. What is new here
 * is saying so on screen, offering the choice when the server refuses to guess,
 * and carrying the requirement's *title* into Position — which the server has
 * never done, because `applied_position` comes only from the call or from
 * whatever somebody typed.
 */
function TrackDialog({
  candidate,
  saving,
  onCancel,
  onSave,
}: {
  candidate: Candidate;
  saving: boolean;
  onCancel: () => void;
  onSave: (values: {
    clientId: string;
    position: string;
    jobOpeningId: string | null;
  }) => void;
}) {
  const [clientId, setClientId] = useState(candidate.latestCallClientId ?? '');
  const [position, setPosition] = useState('');
  const [openingId, setOpeningId] = useState('');
  /**
   * Whether the Position box holds a requirement's title rather than something
   * typed. A client change may replace what this form put there; it must never
   * replace what a person put there.
   */
  const [fromRequirement, setFromRequirement] = useState(true);

  /**
   * The client's open requirements. Open only, for the reason the server gives:
   * a closed requirement is not something a candidate is being submitted to
   * now, and back-filling into one reopens a decision already made.
   */
  const requirements = useQuery<Page<JobOpeningWithPipeline>>({
    queryKey: ['job-openings', 'open-for-client', clientId],
    queryFn: async () =>
      (
        await client.get('/api/v1/job-openings', {
          params: { clientId, openOnly: true, limit: 50 },
        })
      ).data,
    enabled: Boolean(clientId),
  });
  const openings = requirements.data?.items ?? [];
  const resolved = Boolean(clientId) && requirements.isSuccess;

  /**
   * Resolve the requirement whenever the answer could have changed.
   *
   * Keyed on the loaded list rather than on the click, so it is right whether
   * the client was chosen here or arrived prefilled from the candidate's last
   * call. A failed fetch stays "none": the track still goes through unlinked,
   * which is exactly what happens today for a client with nothing logged.
   */
  useEffect(() => {
    if (!resolved) return;
    const sole = openings.length === 1 ? openings[0] : null;
    setOpeningId(sole ? sole.id : '');
    if (!fromRequirement) return;
    setPosition(sole ? sole.title : '');
    // fromRequirement is read, not tracked: it says whether this effect may
    // write, and listing it would re-run the effect on the write itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved, requirements.data, clientId]);

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
        <h3 className="text-sm font-semibold text-gray-900">
          Track {titleCaseName(candidate.fullName)}
        </h3>
        {/* Says what the action is for, since "track" is a word the product
            uses in three places. */}
        <p className="mt-0.5 text-xs text-gray-500">
          Submits them to a client, which is what puts them on the Joining
          Tracker and makes them selectable when scheduling an interview.
        </p>

        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            onSave({ clientId, position, jobOpeningId: openingId || null });
          }}
        >
          <div>
            <label className="label" htmlFor="tr-client">Client</label>
            <ClientSelect
              purpose="work"
              id="tr-client"
              value={clientId}
              required
              attachedId={candidate.latestCallClientId}
              onChange={(next) => {
                setClientId(next);
                // The new client's requirements decide again. Cleared here
                // rather than left stale, so a submit landing before the fetch
                // returns cannot file this candidate against the old client's
                // requirement.
                setOpeningId('');
              }}
            />
            {candidate.latestCallClientName && (
              <p className="mt-1 text-xs text-gray-500">
                Their last call was about {candidate.latestCallClientName}.
              </p>
            )}
          </div>

          {/* Only when there is a choice to make. One requirement has already
              filled the box below and named itself; none has nothing to ask. */}
          {resolved && openings.length > 1 && (
            <div>
              <label className="label" htmlFor="tr-requirement">Requirement</label>
              <select
                id="tr-requirement"
                className="input"
                value={openingId}
                onChange={(e) => {
                  const chosen = openings.find((o) => o.id === e.target.value);
                  setOpeningId(chosen ? chosen.id : '');
                  if (chosen) {
                    setPosition(chosen.title);
                    setFromRequirement(true);
                  }
                }}
              >
                {/* Never required. A candidate can go forward to a client
                    without being filed against one of their open roles. */}
                <option value="">Not one of these</option>
                {openings.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.title}
                    {o.location ? ` · ${o.location}` : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {openings.length} requirements are open for this client. Naming
                one puts this candidate in its pipeline.
              </p>
            </div>
          )}

          <div>
            <label className="label" htmlFor="tr-position">Position</label>
            <input
              spellCheck={false}
              id="tr-position"
              className="input"
              maxLength={160}
              value={position}
              onChange={(e) => {
                setPosition(e.target.value);
                // Typed over, so no later client change may replace it. An
                // emptied box goes back to being this form's to fill.
                setFromRequirement(e.target.value.trim() === '');
              }}
              placeholder="Phlebotomist"
            />
            <p className="mt-1 text-xs text-gray-500">
              {resolved && openings.length === 1 && fromRequirement
                ? 'From this client’s one open requirement. Type over it if the role is different.'
                : resolved && openings.length === 0
                  ? 'No open requirement is logged for this client, so this one is yours to type.'
                  : 'Used only if the call did not already record one.'}
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn-ghost" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Track
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * What a recruiter can promise about a resume.
 *
 * "Today" is dropped after 18:25 rather than offered and refused. The deadline
 * would be 18:30 and the reminder five minutes before it, so past that point
 * the option is a commitment to a moment that has already gone. The server
 * refuses it too — this is the courtesy, not the guard.
 */
function resumeChoices(now: Date): { value: ResumeChoice; label: string }[] {
  const past1825 = now.getHours() > 18 || (now.getHours() === 18 && now.getMinutes() >= 25);
  return [
    { value: '2h', label: 'In 2 hours' },
    { value: '4h', label: 'In 4 hours' },
    { value: '8h', label: 'In 8 hours' },
    ...(past1825 ? [] : [{ value: 'today' as const, label: 'Today' }]),
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'received', label: 'Resume received' },
  ];
}

/** What the row shows now: a promise, a receipt, or nothing said. */
function resumeState(candidate: Candidate): { label: string; tone: string } {
  if (candidate.resumeReceivedAt) {
    return { label: 'Received', tone: 'bg-emerald-100 text-emerald-700' };
  }
  if (!candidate.resumeDueAt) {
    return { label: 'Not set', tone: 'bg-white text-gray-400 ring-1 ring-gray-200' };
  }
  const due = new Date(candidate.resumeDueAt);
  const label = due.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
  // Overdue is stated, not implied by a colour alone.
  return due < new Date()
    ? { label: `Overdue · ${label}`, tone: 'bg-red-100 text-red-700' }
    : { label: `By ${label}`, tone: 'bg-amber-100 text-amber-800' };
}

/**
 * The notice periods the form offers, and the days each one stores.
 *
 * A bucket has to become a number because notice_period_days is what the
 * "available within N days" filter compares, and a filter cannot read a label.
 * Each stores the *upper* bound of its range: "15-30 days" saves 30, so asking
 * for anybody available within a month includes them and asking for within a
 * fortnight does not. Storing the lower bound would promise a candidate is
 * free sooner than they said.
 */
const NOTICE_PERIODS = [
  { label: '1-2 weeks', days: 14 },
  { label: '15-30 days', days: 30 },
  { label: '30-90 days', days: 90 },
] as const;

/** The submission statuses the Status column can show, in pipeline order. */
const SUBMISSION_STATUSES: SubmissionStatus[] = [
  'pending', 'shortlisted', 'selected', 'rejected', 'hold', 'followup',
  'yet_to_join', 'working',
];

/** The interview modes the Mode column can show. */
const INTERVIEW_MODES: InterviewMode[] = ['telephonic', 'virtual', 'f2f'];

/**
 * An empty cell.
 *
 * Deliberately identical whether the field is empty or was blanked for this
 * viewer by _redact_for. Distinguishing them would announce that there is
 * something to hide, which is the opposite of what the redaction is for.
 */
/**
 * Where the Interested-only filter is remembered.
 *
 * It used to default to on and reset to on with every reload, which on a
 * company that had imported candidates hid 2,296 of 2,329 people behind a tick
 * nobody had set — indistinguishable from data loss, and reported as such.
 *
 * This screen is the company's candidate list: everybody, imported or added by
 * hand or created from a call, and nobody drops off it for being placed. The
 * narrowing to a call-based worklist is a thing you ask for, so it is off
 * until asked for and remembered once it has been.
 */
const INTERESTED_ONLY_KEY = 'candidates.interestedOnly';

function readInterestedOnly(): boolean {
  try {
    return window.localStorage.getItem(INTERESTED_ONLY_KEY) === 'true';
  } catch {
    // Private browsing, or storage disabled. The filter still works for this
    // session; only the memory of it is lost.
    return false;
  }
}

function rememberInterestedOnly(on: boolean): void {
  try {
    window.localStorage.setItem(INTERESTED_ONLY_KEY, String(on));
  } catch {
    // As above. Not worth telling anybody about.
  }
}

/**
 * Rows per page. The endpoint caps limit at 200; fifty is what this screen has
 * always asked for and the number the paging is built around.
 */
const PAGE_SIZE = 50;

function Blank() {
  return <span className="text-gray-300">—</span>;
}

export default function CandidatesPage() {
  /**
   * Whether the list names whose work each row is, and can be narrowed by it.
   *
   * Declared first because three queries below are gated on it. The same
   * predicate the Joining Tracker and the Daily Calls Report use, so the
   * three screens agree about who may see the names.
   */
  const monitors = seesOthersWork(useAuth((state) => state.user));
  const [search, setSearch] = useState('');
  const [mineOnly, setMineOnly] = useState(false);
  /**
   * Whether to narrow to candidates whose most recent call was positive.
   *
   * True by default: that is what this screen has always been, and what makes
   * it a list somebody works rather than the whole database. Off, it is every
   * candidate in the company — which is the only way to see people who
   * arrived by import, because an import creates no calls and so nobody it
   * adds can have a most recent one.
   */
  const [interestedOnly, setInterestedOnly] = useState(readInterestedOnly);

  /** Set it and remember it, so a reload does not quietly re-narrow the list. */
  function chooseInterestedOnly(on: boolean) {
    setInterestedOnly(on);
    rememberInterestedOnly(on);
  }
  /**
   * Where in the book we are.
   *
   * The list was fifty rows with no way past them and a total above saying
   * 2,297 — fifty presented as though it were all of them, which is worse
   * than an empty page because it looks complete.
   */
  const [offset, setOffset] = useState(0);
  /**
   * Server-side, all four. The list is drawn from the same call book the
   * Daily Calls Report reads, so narrowing a loaded page of fifty would
   * search the fifty that arrived rather than the work.
   *
   * Status here means the most recent call's outcome — the field that decides
   * membership of this list in the first place. Not the submission status,
   * which is the tracker's, and not the resume state, which has its own
   * column two along.
   */
  const [recruiterId, setRecruiterId] = useState('');
  const [clientId, setClientId] = useState('');
  const [appliedPosition, setAppliedPosition] = useState('');
  const [submissionStatus, setSubmissionStatus] = useState('');
  const [interviewFrom, setInterviewFrom] = useState('');
  // What is in the box, which is not the same as what it has parsed to:
  // a half-typed date leaves the filter empty while the text stays.
  const [interviewFromInput, setInterviewFromInput] = useState('');
  const [interviewTo, setInterviewTo] = useState('');
  const [interviewToInput, setInterviewToInput] = useState('');
  const [interviewMode, setInterviewMode] = useState('');
  const [qualification, setQualification] = useState('');
  const [prefLocation, setPrefLocation] = useState('');
  const [expMin, setExpMin] = useState('');
  const [expMax, setExpMax] = useState('');
  const [noticeMax, setNoticeMax] = useState('');
  const anyFilter = Boolean(
    recruiterId || clientId || appliedPosition || submissionStatus
    || interviewFrom || interviewTo || interviewMode || qualification
    || prefLocation || expMin || expMax || noticeMax,
  );
  // The typed filters wait for a pause; they are in the query key, so without
  // this each keystroke is its own request against the whole candidate table.
  const qualificationTerm = useDebounced(qualification);
  const prefLocationTerm = useDebounced(prefLocation);
  const appliedPositionTerm = useDebounced(appliedPosition);
  const [filesFor, setFilesFor] = useState<Candidate | null>(null);
  /** Whose full record the read-only panel is showing. */
  const [viewing, setViewing] = useState<Candidate | null>(null);
  const [importing, setImporting] = useState(false);
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Candidate | null>(null);
  const [tracking, setTracking] = useState<Candidate | null>(null);

  /**
   * The same endpoint the Calls page Track button uses, on this candidate's
   * most recent call — which is the call that put them on this page.
   */
  const track = useMutation({
    mutationFn: async ({
      candidate,
      values,
    }: {
      candidate: Candidate;
      values: { clientId: string; position: string; jobOpeningId: string | null };
    }) =>
      (
        await client.post(`/api/v1/calls/${candidate.latestCallId}/submit`, {
          clientId: values.clientId,
          position: values.position.trim() || null,
          // Named only when the dialog resolved one. Omitting it leaves the
          // server's own _sole_open_requirement to decide, which is the same
          // answer for the one-requirement case and the right answer for none.
          jobOpeningId: values.jobOpeningId,
        })
      ).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      void queryClient.invalidateQueries({ queryKey: ['tracker'] });
      void queryClient.invalidateQueries({ queryKey: ['open-submissions'] });
      setTracking(null);
      toast.success('Submitted — they can now be scheduled for an interview');
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not track that candidate')),
  });

  /**
   * Fetched for the edit dialog, and now for the client filter as well.
   *
   * The `enabled` gate widens rather than being dropped: a recruiter still
   * only pays for this list when they open a dialog, because they get no
   * filter bar to spend it on.
   */
  const { data: clientList } = useQuery<Page<ClientRow>>({
    queryKey: ['clients', 'for-edit'],
    queryFn: async () =>
      (await client.get('/api/v1/clients', { params: { limit: 200 } })).data,
    enabled: editing !== null || tracking !== null || monitors,
  });


  /**
   * Two writes from one Save, because the four fields have two homes.
   *
   * Sequential and in this order on purpose: the candidate fields are the
   * substance of the edit and the client is a correction to a different
   * record. If the second fails the first has still landed, and saying so
   * plainly beats a single "saved" that is two-thirds true — or a rollback
   * this endpoint pair cannot honestly offer.
   */
  const save = useMutation({
    mutationFn: async ({
      candidate,
      values,
    }: {
      candidate: Candidate;
      values: {
        altPhone: string;
        email: string;
        certificates: string[];
        hadPt: boolean;
        hadEsi: boolean;
        hadTds: boolean;
        qualification: string;
        experienceMonths: string;
        currentLocation: string;
        preferredLocation: string;
        currentCompany: string;
        designation: string;
        currentlyEmployed: string;
        currentSalary: string;
        expectedSalary: string;
        noticePeriodDays: string;
        dateOfBirth: string;
        clientId: string;
        customValues: CustomValues;
      };
    }) => {
      await client.patch(`/api/v1/candidates/${candidate.id}`, {
        qualification: values.qualification.trim() || null,
        experienceMonths:
          values.experienceMonths === '' ? null : Number(values.experienceMonths),
        currentLocation: values.currentLocation.trim() || null,
        preferredLocation: values.preferredLocation.trim() || null,
        currentCompany: values.currentCompany.trim() || null,
        expectedSalary: values.expectedSalary.trim() || null,
        noticePeriodDays:
          values.noticePeriodDays.trim() === '' ? null : Number(values.noticePeriodDays),
        altPhone: values.altPhone.trim() || null,
        email: values.email.trim() || null,
        hadPt: values.hadPt,
        hadEsi: values.hadEsi,
        hadTds: values.hadTds,
        // Only sent when this viewer may read the field. The server ignores a
        // write to something the caller cannot read, so sending it anyway
        // would be a no-op that looks like a save — and the value being sent
        // would be the blank the redacted response supplied.
        ...(mayEditDob ? { dateOfBirth: values.dateOfBirth.trim() || null } : {}),
        ...(mayEditSalary(candidate)
          ? { currentSalary: values.currentSalary.trim() || null }
          : {}),
        designation: values.designation.trim() || null,
        // Three states through to the API: "" is null, meaning nobody asked.
        currentlyEmployed:
          values.currentlyEmployed === '' ? null : values.currentlyEmployed === 'true',
        customValues: values.customValues,
      });

      // Diffed rather than replaced: the endpoints take one row at a time,
      // and rewriting every certificate on every save would churn ids that
      // nothing else has a reason to change.
      const before = (
        queryClient.getQueryData<CandidateCertificate[]>([
          'candidates', candidate.id, 'certificates',
        ]) ?? []
      );
      const after = values.certificates;
      for (const row of before.filter((c) => !after.includes(c.name))) {
        await client.delete(
          `/api/v1/candidates/${candidate.id}/certificates/${row.id}`,
        );
      }
      for (const name of after.filter(
        (n) => !before.some((c) => c.name === n),
      )) {
        await client.post(`/api/v1/candidates/${candidate.id}/certificates`, {
          name,
        });
      }

      const clientChanged =
        (values.clientId || null) !== (candidate.latestCallClientId ?? null);
      if (candidate.latestCallId && clientChanged) {
        try {
          await client.patch(`/api/v1/calls/${candidate.latestCallId}`, {
            clientId: values.clientId || null,
          });
        } catch (error) {
          throw new Error(
            `Details saved, but the client could not be changed: ${errorMessage(error)}`,
          );
        }
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      void queryClient.invalidateQueries({ queryKey: ['calls'] });
      setEditing(null);
      toast.success('Saved');
    },
    onError: (e) => {
      // The candidate half may well have landed, so the list is refreshed
      // either way rather than left showing values the server no longer holds.
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      toast.error(errorMessage(e, 'Could not save that'));
    },
  });


  /**
   * Records when the resume is promised, or that it arrived.
   *
   * Writes only to the candidate. It reaches no submission, no offer and no
   * placement — the reason this control exists rather than one that sets a
   * pipeline status, and the reason no choice here can move the Joining
   * Tracker.
   */
  const setCommitment = useMutation({
    mutationFn: async ({ id, choice }: { id: string; choice: ResumeChoice }) =>
      (
        await client.post(`/api/v1/candidates/${id}/resume-commitment`, {
          choice,
        })
      ).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      // The reminder is keyed to the deadline, so it should reflect the new
      // one without waiting for the next poll.
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not set that')),
  });
  // POST /imports is company-admin-gated server-side. Showing a manager a
  // button that can only 403 is worse than not showing it — the same rule the
  // export button on Calls follows.
  const me = useAuth((state) => state.user);
  const canImport = isCompanyAdmin(me);
  //: The same rule _redacted_fields_for applies on the server. Company Admin
  //: and Owner see the date of birth; they and the owning recruiter see the
  //: present salary. Kept in one expression so the form offers exactly the
  //: fields the API will accept a write to.
  const privileged = isCompanyAdmin(me);
  /**
   * The candidate whose delete is being confirmed.
   *
   * Company admin and owner only, and the API enforces the same — the button
   * being absent is courtesy, the 403 behind it is the rule.
   */
  const [deleting, setDeleting] = useState<Candidate | null>(null);
  const mayEditDob = privileged;
  const mayEditSalary = (row: Candidate) =>
    privileged || row.ownerUserId === me?.id;

  /**
   * What "the rows on screen" means, as one object.
   *
   * Built once and used twice — by the list query and by the Excel download —
   * so the file cannot contain a different set than the table. Two copies of
   * fourteen filters is how an export quietly stops matching the screen above
   * it, which is exactly what this replaces.
   */
  const listFilters: Record<string, string | number | boolean | undefined> = {
    search: search || undefined,
    mineOnly,
    ...(recruiterId ? { recruiterId } : {}),
    // submissionClientId, not clientId: the Client column reads the latest
    // submission, and a filter answering about the latest call would
    // contradict the cell above it.
    ...(clientId ? { submissionClientId: clientId } : {}),
    ...(appliedPositionTerm.trim()
      ? { appliedPosition: appliedPositionTerm.trim() } : {}),
    ...(submissionStatus ? { submissionStatus } : {}),
    ...(interviewFrom ? { interviewFrom } : {}),
    ...(interviewTo ? { interviewTo } : {}),
    ...(interviewMode ? { interviewMode } : {}),
    // Server-side, like every other filter here: this is one page of the
    // company's candidates, so narrowing what already arrived would search
    // fifty of them and present it as the answer.
    ...(qualificationTerm.trim()
      ? { qualification: qualificationTerm.trim() } : {}),
    ...(prefLocationTerm.trim()
      ? { preferredLocation: prefLocationTerm.trim() } : {}),
    ...(expMin ? { experienceMinMonths: Number(expMin) } : {}),
    ...(expMax ? { experienceMaxMonths: Number(expMax) } : {}),
    ...(noticeMax ? { noticePeriodMaxDays: Number(noticeMax) } : {}),
    // Most recent call, not "has ever been interested". Somebody who said yes
    // in June and was unreachable last week is not on this list, and the
    // server decides that so the page cannot disagree with the report it came
    // from. The download inherits it rather than widening to every candidate.
    //
    // Off by default. A candidate who arrived by import has no calls at all
    // and is invisible under this, which is every row of a two-thousand line
    // upload — so defaulting it on made the company's candidate list look
    // empty. Remembered when it is turned on; see INTERESTED_ONLY_KEY.
    ...(interestedOnly ? { latestCallPositive: true } : {}),
  };

  /**
   * Every value the list narrows by, in one place.
   *
   * In the query key, or toggling one would serve the previous answer from
   * cache. Also what the page reset watches, below — a filter added later
   * joins both the moment it joins this list.
   */
  const filterValues = [
    interestedOnly,
    search,
    mineOnly,
    recruiterId,
    clientId,
    appliedPositionTerm,
    submissionStatus,
    interviewFrom,
    interviewTo,
    interviewMode,
    qualificationTerm,
    prefLocationTerm,
    expMin,
    expMax,
    noticeMax,
  ];

  /**
   * Setting any filter returns to the first page.
   *
   * The calls report wraps each setter (065f104). This screen has fifteen
   * filters, which is fifteen chances to forget one, and the bug that makes
   * would read as "the filter found nothing" rather than "you are past the
   * end" — so the reset watches the values instead of the setters and cannot
   * be forgotten. Adjusting state during render is React's documented way to
   * do this; an effect would fetch once at the stale offset before correcting
   * itself.
   */
  const filterKey = JSON.stringify(filterValues);
  const [lastFilterKey, setLastFilterKey] = useState(filterKey);
  if (filterKey !== lastFilterKey) {
    setLastFilterKey(filterKey);
    setOffset(0);
  }

  const canExport = isSupervisor(me);

  const exportCandidates = useMutation({
    mutationFn: async () =>
      downloadExport({
        kind: 'candidates',
        format: 'xlsx',
        params: listFilters,
      }),
    onSuccess: (name) => toast.success(`Saved ${name}`),
    onError: (error) => {
      void exportErrorMessage(error).then((message) => toast.error(message));
    },
  });

  const { data, isLoading } = useQuery<Page<Candidate>>({
    queryKey: ['candidates', 'interested', ...filterValues, offset],
    queryFn: async () =>
      (
        await client.get('/api/v1/candidates', {
          params: { ...listFilters, limit: PAGE_SIZE, offset },
        })
      ).data,
  });

  return (
    <div className="space-y-4">
      <div className="rounded-xl border-2 border-indigo-100 bg-white p-5 shadow-sm">
        <h1 className="text-2xl font-bold text-gray-900">
          Interview Candidates
        </h1>
        {/* Said on the page, not only in the tab. This is a filtered view of a
            much larger table, and somebody who has just imported five hundred
            candidates needs to know why they are not here. */}
        <p className="text-sm text-gray-500">
          {interestedOnly ? (
            <>
              Candidates whose most recent call was Interested or Shortlisted.
              Someone who was interested earlier but has since gone quiet is
              not here, and neither is anybody who has not been called yet —
              untick <span className="font-medium">Interested only</span> to
              see everybody, including imported candidates.
            </>
          ) : (
            <>
              Every candidate in the company, including anybody who arrived by
              import and has never been called. Tick{' '}
              <span className="font-medium">Interested only</span> to narrow it
              back to the worklist.
            </>
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute top-2.5 left-3 h-4 w-4 text-gray-400" />
          <input
            spellCheck={false}
            className="input pl-9"
            placeholder="Search by name, phone or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={mineOnly}
            onChange={(e) => setMineOnly(e.target.checked)}
            className="rounded border-gray-300"
          />
          Only mine
        </label>
        {/* The narrowing this screen *is*, made switchable rather than
            assumed.

            The list has always been "whose most recent call was Interested or
            Shortlisted", which is the right default and is what makes it a
            worklist. But an imported candidate has no calls at all, so an
            import of two thousand people lands here and shows nothing — and
            the only way to see them was to know about a different screen.
            Unticking this asks for the whole table instead. */}
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={interestedOnly}
            onChange={(e) => chooseInterestedOnly(e.target.checked)}
            className="rounded border-gray-300"
          />
          Interested only
        </label>
        {canImport && (
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => setImporting(true)}
            title="Import a tracker workbook — tabs are matched by their headers"
          >
            <Upload className="h-3.5 w-3.5" />
            Upload Excel
          </button>
        )}
      </div>

      {/* The tracker's filter bar, the same shape as the one on the Daily
          Calls Report — label wrapping a select, an "Any" empty option, and
          a Clear that only appears once something is set. Three screens onto
          the same people's work should not need learning three times.

          Shown to everyone, where it was supervisors only. A recruiter
          shortlisting against a requirement needs to narrow by qualification
          and notice period exactly as a manager does, and every control here
          except Recruiter narrows a list they can already see. */}
      <section className="card border-2 border-indigo-100 shadow-sm [&_label]:text-sm [&_label]:font-semibold [&_label]:text-gray-700">
          <div className="flex flex-wrap items-end gap-3">
            {monitors && (
              <RecruiterFilter
                className="w-40"
                value={recruiterId}
                onChange={setRecruiterId}
              />
            )}

            <label className="w-44 text-xs text-gray-600">
              Qualification
              <input
                type="search"
                className="input mt-1 py-1 text-xs"
                placeholder="Any"
                value={qualification}
                onChange={(e) => setQualification(e.target.value)}
              />
            </label>

            <label className="w-44 text-xs text-gray-600">
              Preferred Location
              <input
                type="search"
                className="input mt-1 py-1 text-xs"
                placeholder="Anywhere"
                value={prefLocation}
                onChange={(e) => setPrefLocation(e.target.value)}
              />
            </label>

            {/* Bounds, because nobody screens for exactly three years. Months
                rather than years: it is the unit the column stores, and
                converting here would round somebody out of a shortlist.

                The unit sits in the placeholder rather than the label. Three
                controls reading "Exp. min (months)" made the longest labels in
                the bar the least important ones, and pushed the row that
                matters onto a second line. */}
            <label className="w-24 text-xs text-gray-600">
              Exp. Min
              <input
                type="number"
                min={0}
                max={720}
                placeholder="months"
                className="input mt-1 py-1 text-xs"
                value={expMin}
                onChange={(e) => setExpMin(e.target.value)}
              />
            </label>

            <label className="w-24 text-xs text-gray-600">
              Exp. Max
              <input
                type="number"
                min={expMin || 0}
                max={720}
                placeholder="months"
                className="input mt-1 py-1 text-xs"
                value={expMax}
                onChange={(e) => setExpMax(e.target.value)}
              />
            </label>

            <label className="w-28 text-xs text-gray-600">
              Notice Within
              <input
                type="number"
                min={0}
                max={365}
                placeholder="days"
                className="input mt-1 py-1 text-xs"
                value={noticeMax}
                onChange={(e) => setNoticeMax(e.target.value)}
              />
            </label>

            {/* From here down, every control filters a column shown in the
                table, and reads the same row that column reads — the latest
                submission and the interview on it. The previous Status,
                Client and date controls read the latest *call*, which stopped
                matching the moment those columns did. */}
            <label className="w-40 text-xs text-gray-600">
              Client
              <ClientSelect
                purpose="work"
                className="input mt-1 py-1 text-xs"
                value={clientId}
                onChange={setClientId}
                placeholder="Any client"
                // A filter, not a picker: it narrows rows somebody is reading,
                // and the several hundred names the importer created would
                // bury the ones anybody typed in. Those clients are still
                // offered wherever a new record is created.
                includeImported={false}
              />
            </label>

            <label className="w-40 text-xs text-gray-600">
              Applied Position
              <input
                type="search"
                className="input mt-1 py-1 text-xs"
                placeholder="Any role"
                value={appliedPosition}
                onChange={(e) => setAppliedPosition(e.target.value)}
              />
            </label>

            <label className="w-40 text-xs text-gray-600">
              Status
              <select
                className="input mt-1 py-1 text-xs"
                value={submissionStatus}
                onChange={(e) => setSubmissionStatus(e.target.value)}
              >
                <option value="">Any status</option>
                {SUBMISSION_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {value.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </label>

            {/* Typed rather than picked, matching the Daily Calls Report.
                A native date input renders its own lower-case dd-mm-yyyy hint
                that no attribute can reach, so showing DD-MM-YYYY means owning
                the parsing. Worth it for a filter, which somebody types
                quickly knowing the date they want; not worth it for a form
                field, which is why every date on a form is still native. */}
            <label className="w-36 text-xs text-gray-600">
              Interview From
              <input
                type="text"
                inputMode="numeric"
                className="input mt-1 py-1 text-xs"
                placeholder="DD-MM-YYYY"
                value={interviewFromInput || formatFilterDate(interviewFrom)}
                onChange={(e) => {
                  const value = sanitiseFilterDateInput(e.target.value);
                  setInterviewFromInput(value);
                  setInterviewFrom(parseFilterDate(value));
                }}
              />
            </label>

            <label className="w-36 text-xs text-gray-600">
              Interview To
              <input
                type="text"
                inputMode="numeric"
                className="input mt-1 py-1 text-xs"
                placeholder="DD-MM-YYYY"
                value={interviewToInput || formatFilterDate(interviewTo)}
                onChange={(e) => {
                  const value = sanitiseFilterDateInput(e.target.value);
                  setInterviewToInput(value);
                  setInterviewTo(parseFilterDate(value));
                }}
              />
            </label>

            <label className="w-36 text-xs text-gray-600">
              Mode
              <select
                className="input mt-1 py-1 text-xs"
                value={interviewMode}
                onChange={(e) => setInterviewMode(e.target.value)}
              >
                <option value="">Any mode</option>
                {INTERVIEW_MODES.map((value) => (
                  <option key={value} value={value}>
                    {value.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </label>

            {anyFilter && (
              <button
                type="button"
                className="btn-ghost mb-0.5 text-xs"
                onClick={() => {
                  setRecruiterId('');
                  setClientId('');
                  setAppliedPosition('');
                  setSubmissionStatus('');
                  setInterviewFrom('');
                  setInterviewTo('');
                  setInterviewMode('');
                  setQualification('');
                  setPrefLocation('');
                  setExpMin('');
                  setExpMax('');
                  setNoticeMax('');
                }}
              >
                Clear filters
              </button>
            )}

            {/* Beside the filters, because what it downloads is whatever they
                currently say — including the Interested/Shortlisted narrowing
                this whole screen is, which the file inherits rather than
                quietly widening to every candidate in the company. */}
            {canExport && (
              <button
                type="button"
                className="btn-ghost mb-0.5 ml-auto text-xs"
                disabled={exportCandidates.isPending}
                onClick={() => exportCandidates.mutate()}
                title="Downloads the rows matching the filters above"
              >
                {exportCandidates.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                )}
                Excel
              </button>
            )}
          </div>

          {/* Every one of these reads the candidate's most recent call, which
              is the call that put them on this list. Worth saying: a client
              filter will not surface somebody called about that client in June
              and about somebody else last week. */}
          {anyFilter && (
            <p className="mt-2 text-xs text-gray-400">
              Status, client and dates all read each candidate's most recent
              call — the one that put them on this list, not every call ever
              made to them.
            </p>
          )}
      </section>

      {isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <>
          {/* Which rows these are, not just how many exist. "2,297
              candidates" above a table of fifty is the sentence that made
              this look complete. */}
          <p className="text-sm text-gray-500">
            {data && data.total > 0
              ? `${(offset + 1).toLocaleString('en-IN')}–${Math.min(
                  offset + data.items.length,
                  data.total,
                ).toLocaleString('en-IN')} of ${data.total.toLocaleString(
                  'en-IN',
                )} candidates`
              : `${(data?.total ?? 0).toLocaleString('en-IN')} candidates`}
          </p>
          <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  {/* Position in the filtered list, counted through the
                      pages rather than restarting at each one. Still not an
                      identity: it renumbers when the filters change. It is a
                      place to point at across a desk, not a key -- the
                      Candidate ID column on the export is the stable one. */}
                  <th className="px-4 py-2.5 font-medium">Sr. No.</th>
                  <th className="px-4 py-2.5 font-medium">Candidate Full Name</th>
                  {monitors && (
                    <th className="px-4 py-2.5 font-medium">Recruiter</th>
                  )}
                  <th className="px-4 py-2.5 font-medium">Mobile</th>
                  {/* This screen is a view onto submissions — somebody is here
                      because they went to a client — so the date, the client
                      and the role are what tells two rows for one person
                      apart. Labelled Submitted rather than Date: there are two
                      dates on this row and "Date" beside "Interview" says
                      nothing about which is which. */}
                  <th className="px-4 py-2.5 font-medium">Submitted</th>
                  <th className="px-4 py-2.5 font-medium">Client</th>
                  <th className="px-4 py-2.5 font-medium">Applied Position</th>
                  <th className="px-4 py-2.5 font-medium">Interview</th>
                  <th className="px-4 py-2.5 font-medium">Mode</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>

                  {/* The shortlisting fields, back on the table.
                      They were moved to the detail panel on the argument that
                      shortlisting happens before this tab and the filter bar
                      is the better way to use them. That holds for filtering
                      and not for reading: somebody checking an imported sheet
                      against the screen needs the columns the sheet had.
                      Horizontal scroll is the accepted cost.

                      Date of birth and present salary are here too, and are
                      safe to be: _redact_for blanks them server-side — DOB to
                      Company Admin and Owner, present salary to those two and
                      the owning recruiter — so the cell is empty for anybody
                      without a stated use rather than the column being absent
                      for everybody. The access rule is unchanged; only its
                      visibility is. */}
                  <th className="px-4 py-2.5 font-medium">Email</th>
                  <th className="px-4 py-2.5 font-medium">Alt Mobile</th>
                  <th className="px-4 py-2.5 font-medium">DOB</th>
                  <th className="px-4 py-2.5 font-medium">Qualification</th>
                  <th className="px-4 py-2.5 font-medium">Experience</th>
                  <th className="px-4 py-2.5 font-medium">Designation</th>
                  <th className="px-4 py-2.5 font-medium">Present Company</th>
                  <th className="px-4 py-2.5 font-medium">Employed</th>
                  <th className="px-4 py-2.5 font-medium">Staying Location</th>
                  <th className="px-4 py-2.5 font-medium">Preferred Location</th>
                  <th className="px-4 py-2.5 font-medium">Notice</th>
                  <th className="px-4 py-2.5 font-medium">Present Salary</th>
                  <th className="px-4 py-2.5 font-medium">Expected Salary</th>
                  <th className="px-4 py-2.5 font-medium">Latest Call</th>
                  <th className="px-4 py-2.5 font-medium">Call Client</th>
                  <th className="px-4 py-2.5 font-medium">Source</th>
                  <th className="px-4 py-2.5 font-medium">Notes</th>
                  <th className="px-4 py-2.5 font-medium">Added</th>

                  <th className="px-4 py-2.5 font-medium">Resume</th>
                  <th className="px-4 py-2.5 font-medium">View</th>
                  <th className="px-4 py-2.5 font-medium">Track</th>
                  <th className="px-4 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data?.items.map((candidate, index) => (
                  <tr key={candidate.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 align-top tabular-nums text-gray-400">
                      {offset + index + 1}
                    </td>
                    <td className="px-4 py-2.5 align-top font-medium text-gray-900">
                      {titleCaseName(candidate.fullName)}
                    </td>
                    {monitors && (
                      <td className="whitespace-nowrap px-4 py-2.5 align-top text-gray-600">
                        {titleCaseName(candidate.ownerName) ?? (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-2.5">
                      {candidate.phone ? (
                        <a
                          href={`tel:${telHref(candidate.phone)}`}
                          className="inline-flex items-center gap-1 whitespace-nowrap text-indigo-600 hover:underline"
                        >
                          <Phone className="h-3.5 w-3.5" />
                          {formatPhone(candidate.phone)}
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {candidate.latestSubmissionSubmittedOn ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {candidate.latestSubmissionClientName ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {candidate.latestSubmissionAppliedPosition ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {candidate.latestInterviewOn ?? (
                        <span className="text-gray-300">not scheduled</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {candidate.latestInterviewMode
                        ? candidate.latestInterviewMode.replace(/_/g, ' ')
                        : '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {candidate.latestSubmissionStatus
                        ? candidate.latestSubmissionStatus.replace(/_/g, ' ')
                        : '—'}
                    </td>

                    {/* The columns the sheet has. A blank cell here is either
                        an empty field or one _redact_for blanked for this
                        viewer — indistinguishable on purpose, since saying
                        "hidden from you" would leak that there is something
                        to hide. */}
                    <td className="px-4 py-2.5 text-gray-600">
                      {candidate.email ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-600">
                      {formatPhone(candidate.altPhone) ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {candidate.dateOfBirth ?? <Blank />}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {candidate.qualification ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {/* The recruiter's own words where there are any, since
                          "3.6" and "3 yrs 6 mo" both parse to 42 months and
                          only one of them is what somebody wrote down. */}
                      {candidate.experienceRaw
                        ?? (candidate.experienceMonths != null
                          ? `${candidate.experienceMonths} mo`
                          : <Blank />)}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {candidate.designation ?? <Blank />}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {candidate.currentCompany ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {/* Three states: yes, no, and nobody recorded it. */}
                      {candidate.currentlyEmployed === null
                        ? <Blank />
                        : candidate.currentlyEmployed ? 'Yes' : 'No'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {candidate.currentLocation ?? <Blank />}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {candidate.preferredLocation ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-600">
                      {candidate.noticePeriodDays != null
                        ? `${candidate.noticePeriodDays} d`
                        : <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-600">
                      {candidate.currentSalary ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-600">
                      {candidate.expectedSalary ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {candidate.latestCallOutcome
                        ? candidate.latestCallOutcome.replace(/_/g, ' ')
                        : <Blank />}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {candidate.latestCallClientName ?? <Blank />}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {candidate.source ?? <Blank />}
                    </td>
                    <td className="max-w-xs truncate px-4 py-2.5 text-gray-600" title={candidate.notes ?? undefined}>
                      {candidate.notes ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {candidate.createdAt.slice(0, 10)}
                    </td>
                    {/* Where the recruiter is with this person, and nothing a
                        client has decided. Selecting somebody is the Track
                        action on a call, which creates a real submission. */}
                    {/* Two stacked controls, so the cell is sized for the
                        wider of them and aligned to the top like its
                        neighbours. It carried whitespace-nowrap while holding
                        a badge above a full-width select, which is what
                        clipped the longer labels. */}
                    <td className="w-48 min-w-48 px-4 py-2.5 align-top">
                      {/* The current promise is shown as text and the dropdown
                          sets a new one. A select whose value is a deadline
                          cannot list deadlines as its options, and pretending
                          otherwise would mean inventing an option per row. */}
                      <span
                        className={`badge inline-block max-w-full truncate ${resumeState(candidate).tone}`}
                        title={
                          candidate.resumeReceivedAt
                            ? 'Resume is in hand'
                            : candidate.resumeDueAt
                              ? 'You will be reminded five minutes before'
                              : 'No promise made yet'
                        }
                      >
                        {resumeState(candidate).label}
                      </span>
                      <select
                        className="input mt-1.5 w-full py-0.5 text-xs"
                        value=""
                        disabled={setCommitment.isPending}
                        onChange={(e) => {
                          const choice = e.target.value as ResumeChoice;
                          if (choice) {
                            setCommitment.mutate({ id: candidate.id, choice });
                          }
                        }}
                      >
                        <option value="">Set a deadline…</option>
                        {resumeChoices(new Date()).map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 align-top">
                      <button
                        type="button"
                        className="btn-ghost text-xs"
                        onClick={() => setViewing(candidate)}
                        title="See everything recorded about this candidate"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                    </td>

                    {/* Its own column. It shared one with the pencil and
                        Files under a header reading "Files", which is three
                        controls and one word — and the middle of them is the
                        one somebody is looking for. */}
                    <td className="whitespace-nowrap px-4 py-2.5 align-top">
                      {/* State rather than a hidden button. "Why can I not
                          schedule an interview for this person" is the question
                          that produced this column, and an absent control does
                          not answer it. */}
                      {candidate.hasSubmission ? (
                        <span className="text-xs text-gray-400">Tracked</span>
                      ) : (
                        <button
                          type="button"
                          className="btn-ghost text-xs"
                          disabled={!candidate.latestCallId}
                          onClick={() => setTracking(candidate)}
                          title="Submit to a client so they can be scheduled for an interview"
                        >
                          <Send className="h-3.5 w-3.5" />
                          Track
                        </button>
                      )}
                    </td>

                    {/* Flex with a gap, so two buttons of different widths do
                        not butt against each other. */}
                    <td className="whitespace-nowrap px-4 py-2.5 align-top">
                      <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="btn-ghost text-xs"
                        onClick={() => setEditing(candidate)}
                        title="Edit qualification, experience, location and client"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      {/* Junk removal. Refuses unless nothing references the
                          candidate, so it can only ever remove an empty
                          record — the dialog says what is blocking it. */}
                      {privileged && (
                        <button
                          type="button"
                          className="btn-ghost text-xs text-red-600"
                          onClick={() => setDeleting(candidate)}
                          title="Delete this candidate, if nothing references them"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {/* Green once a resume is actually attached, so the
                          answer to "have we got their CV" is visible from the
                          row rather than one modal away per candidate.
                          Colour is not the only signal — the title says it
                          too, for anyone who cannot pick green out. */}
                      <button
                        type="button"
                        className={`btn-ghost text-xs ${
                          candidate.hasResume ? 'text-emerald-700' : ''
                        }`}
                        onClick={() => setFilesFor(candidate)}
                        title={
                          candidate.hasResume
                            ? 'A resume is on file'
                            : 'No resume uploaded yet'
                        }
                      >
                        <Paperclip className="h-3.5 w-3.5" />
                        Files
                      </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {data?.items.length === 0 && (
                  <tr>
                    <td colSpan={monitors ? 32 : 31} className="px-4 py-8 text-center text-gray-400">
                      {interestedOnly ? (
                        <>
                          No candidate&rsquo;s most recent call was Interested
                          or Shortlisted. Imported candidates have no calls, so
                          untick <span className="font-medium">Interested
                          only</span> to see them.
                        </>
                      ) : (
                        'No candidates match these filters.'
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Offered whenever there is more than one page, including on the
              last one -- a Previous that vanishes at the end strands whoever
              paged there. Both buttons disable at their edge rather than
              disappearing, so the control does not move under the cursor. */}
          {data && data.total > PAGE_SIZE && (
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                Page {Math.floor(offset / PAGE_SIZE) + 1} of{' '}
                {Math.ceil(data.total / PAGE_SIZE).toLocaleString('en-IN')}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  disabled={offset === 0}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  disabled={offset + PAGE_SIZE >= data.total}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {viewing && (
        <CandidateDetailPanel
          key={viewing.id}
          candidateId={viewing.id}
          name={titleCaseNameOr(viewing.fullName, 'this candidate')}
          onClose={() => setViewing(null)}
        />
      )}

      {filesFor && (
        /* Resume is the only thing worth attaching at this stage. The other
           seven types belong to people who have been placed, and the server
           still accepts all of them — this narrows the offer, not the rule. */
        <DocumentsPanel
          title={titleCaseNameOr(filesFor.fullName, 'Candidate')}
          candidateId={filesFor.id}
          allowedTypes={['resume']}
          onClose={() => setFilesFor(null)}
        />
      )}

      {tracking && (
        <TrackDialog
          key={tracking.id}
          candidate={tracking}
          saving={track.isPending}
          onCancel={() => setTracking(null)}
          onSave={(values) => track.mutate({ candidate: tracking, values })}
        />
      )}

      {deleting && (
        <DeleteRecordDialog
          key={deleting.id}
          noun="candidate"
          checkUrl={`/api/v1/candidates/${deleting.id}/delete-check`}
          deleteUrl={`/api/v1/candidates/${deleting.id}`}
          /* A call has its own delete, so the blocker links to it, filtered
             by the candidate's number. A submission has none here, so it is
             named and counted and no link is offered. */
          linkFor={(blocker) =>
            blocker.entity === 'call' && deleting.phone
              ? {
                  to: `/calls?search=${encodeURIComponent(deleting.phone)}`,
                  label: 'Open calls',
                }
              : null
          }
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void queryClient.invalidateQueries({ queryKey: ['candidates'] });
          }}
        />
      )}

      {editing && (
        <EditDialog
          key={editing.id}
          candidate={editing}
          canSeeDob={mayEditDob}
          canSeeSalary={mayEditSalary(editing)}
          clients={clientList?.items ?? []}
          saving={save.isPending}
          onCancel={() => setEditing(null)}
          onSave={(values) => save.mutate({ candidate: editing, values })}
        />
      )}

      {importing && (
        <ImportPanel
          onClose={() => setImporting(false)}
          title="Import a Tracker Workbook"
        />
      )}
    </div>
  );
}

